/**
 * Inventory: slots, stacking, and the click rules for moving items around.
 *
 * Pure data and rules - no DOM, no rendering. The UI in src/ui/ draws whatever
 * this says and calls back into `leftClickSlot` / `rightClickSlot`, so the
 * fiddly half-stack and merge behaviour is testable without a browser.
 */

import { itemMaxStack } from "../block/items.js";

/** Default stack size. Tools override it - see items.ts. */
export const MAX_STACK = 64;
/** The row along the bottom of the screen, always reachable. */
export const HOTBAR_SIZE = 9;
/** Three more rows, visible only when the inventory is open. */
export const MAIN_SIZE = 27;
export const TOTAL_SLOTS = HOTBAR_SIZE + MAIN_SIZE;

export interface ItemStack {
  /** Block id. Items that are not blocks will need a wider id space later. */
  id: number;
  count: number;
}

export function stackOf(id: number, count: number): ItemStack {
  return { id, count };
}

export class Inventory {
  /**
   * Slots 0-8 are the hotbar, 9-35 the main grid. A null slot is empty; a
   * stack with count <= 0 is never stored, so `slot !== null` means it holds
   * something.
   */
  readonly slots: (ItemStack | null)[];

  constructor(size = TOTAL_SLOTS) {
    this.slots = new Array<ItemStack | null>(size).fill(null);
  }

  get(index: number): ItemStack | null {
    return this.slots[index] ?? null;
  }

  set(index: number, stack: ItemStack | null): void {
    if (index < 0 || index >= this.slots.length) return;
    this.slots[index] = stack !== null && stack.count > 0 ? stack : null;
  }

  /** Total of one item id held across every slot. */
  countOf(id: number): number {
    let total = 0;
    for (const s of this.slots) if (s !== null && s.id === id) total += s.count;
    return total;
  }

  /** How many more of `id` could be stored right now. */
  capacityFor(id: number): number {
    const cap = itemMaxStack(id);
    let room = 0;
    for (const s of this.slots) {
      if (s === null) room += cap;
      else if (s.id === id) room += cap - s.count;
    }
    return room;
  }

  /** True when every one of `count` would fit. Changes nothing. */
  fits(id: number, count: number): boolean {
    return this.capacityFor(id) >= count;
  }

  /**
   * Store items, returning however many did not fit.
   *
   * Partial stacks are topped up before empty slots are used, which is what
   * stops a pickup from scattering one item into three different slots.
   */
  add(id: number, count: number): number {
    let left = count;

    for (let i = 0; i < this.slots.length && left > 0; i++) {
      const s = this.slots[i];
      const cap = itemMaxStack(id);
      if (s === null || s.id !== id || s.count >= cap) continue;
      const room = cap - s.count;
      const moved = Math.min(room, left);
      s.count += moved;
      left -= moved;
    }

    for (let i = 0; i < this.slots.length && left > 0; i++) {
      if (this.slots[i] !== null) continue;
      const moved = Math.min(itemMaxStack(id), left);
      this.slots[i] = { id, count: moved };
      left -= moved;
    }

    return left;
  }

  /**
   * Store items into a range of slots only, obeying a per-slot rule.
   *
   * `add` puts things wherever they will go, which is right for a bag and
   * wrong for everywhere else: a stove has three slots that each take one
   * kind of thing, and armour has four that each take one piece. This is the
   * same "top up partial stacks first, then use empty ones" rule with a fence
   * round it.
   *
   * @returns however many did not fit.
   */
  addWithin(
    id: number, count: number,
    from: number, to: number,
    accepts: (slot: number, id: number) => boolean = () => true,
  ): number {
    let left = count;
    const cap = itemMaxStack(id);
    const first = Math.max(0, from);
    const last = Math.min(this.slots.length, to);

    for (let i = first; i < last && left > 0; i++) {
      const s = this.slots[i];
      if (s === null || s.id !== id || s.count >= cap) continue;
      if (!accepts(i, id)) continue;
      const moved = Math.min(cap - s.count, left);
      s.count += moved;
      left -= moved;
    }

    for (let i = first; i < last && left > 0; i++) {
      if (this.slots[i] !== null) continue;
      if (!accepts(i, id)) continue;
      const moved = Math.min(cap, left);
      this.slots[i] = { id, count: moved };
      left -= moved;
    }

    return left;
  }

  /** Take up to `count` out of one slot. Returns how many were actually taken. */
  takeFromSlot(index: number, count: number): number {
    const s = this.get(index);
    if (s === null) return 0;
    const taken = Math.min(s.count, count);
    s.count -= taken;
    if (s.count <= 0) this.slots[index] = null;
    return taken;
  }

  /**
   * Take `count` of an item from wherever it is in the bag.
   *
   * All or nothing: a bow that fires half an arrow because the bag held one
   * and the shot wanted two would be worse than one that refuses. Returns
   * whether it took them.
   */
  take(id: number, count: number): boolean {
    if (this.countOf(id) < count) return false;
    let left = count;
    for (let i = 0; i < this.slots.length && left > 0; i++) {
      if (this.slots[i]?.id !== id) continue;
      left -= this.takeFromSlot(i, left);
    }
    return true;
  }

  /**
   * What death costs.
   *
   * With `keep` the bag is untouched and nothing is spilled; without it the
   * bag is emptied and everything that was in it comes back to be strewn where
   * the body fell. The rule lives here rather than in the click handler
   * because it is a rule of the game and because it can be checked here.
   *
   * All or nothing on purpose. Half-measures - lose the hotbar, keep the
   * rest - are a setting nobody asked for and another thing to explain.
   *
   * The stacks are copied out. Nothing aliases them once the bag is cleared,
   * so this is belt-and-braces rather than a rule, and there is deliberately
   * no test pretending otherwise: one was written, and it could not fail.
   */
  spillOnDeath(keep: boolean): ItemStack[] {
    if (keep) return [];
    const spilled = this.slots.filter((s): s is ItemStack => s !== null)
      .map((s) => ({ ...s }));
    this.clear();
    return spilled;
  }

  /** Drop everything. Used on respawn later; handy in tests now. */
  clear(): void {
    this.slots.fill(null);
  }
}

/**
 * Left-click a slot while holding `held` on the cursor.
 * Returns what the cursor holds afterwards.
 *
 * Follows the conventions every block game shares: pick up a whole stack from
 * an empty hand, merge into a matching stack, swap against a different item.
 */
/**
 * Send a whole stack somewhere else in one gesture.
 *
 * Ctrl and the left button, which is what was asked for. In the game this
 * borrows from it is shift, and the difference matters not at all except that
 * a player who tries shift first should not be surprised - so the game accepts
 * that too.
 *
 * Moves as much as will fit and leaves the rest where it was: a chest with
 * room for three of your sixty-four takes three. Silently doing nothing when
 * the far side is full is correct and is exactly what a player expects, which
 * is why this returns how many moved rather than a boolean nobody can act on.
 *
 * @returns how many items actually moved.
 */
export function quickMove(
  from: Inventory,
  index: number,
  to: Inventory,
  /** The half-open range of `to` that may be used. */
  first = 0,
  last = Number.MAX_SAFE_INTEGER,
  accepts: (slot: number, id: number) => boolean = () => true,
): number {
  const stack = from.get(index);
  if (stack === null) return 0;

  const leftOver = to.addWithin(stack.id, stack.count, first, last, accepts);
  const moved = stack.count - leftOver;
  if (moved > 0) from.takeFromSlot(index, moved);
  return moved;
}

export function leftClickSlot(
  inv: Inventory,
  index: number,
  held: ItemStack | null,
): ItemStack | null {
  const slot = inv.get(index);

  if (held === null) {
    // Empty hand: take the whole stack.
    inv.set(index, null);
    return slot;
  }

  if (slot === null) {
    inv.set(index, held);
    return null;
  }

  if (slot.id === held.id) {
    // Same item: pour as much as the slot will take, keep the rest.
    const room = itemMaxStack(slot.id) - slot.count;
    const moved = Math.min(room, held.count);
    slot.count += moved;
    const remaining = held.count - moved;
    return remaining > 0 ? { id: held.id, count: remaining } : null;
  }

  // Different items: swap hand and slot.
  inv.set(index, held);
  return slot;
}

/**
 * Right-click a slot while holding `held`.
 *
 * Empty hand takes half (rounded up); a full hand puts down one at a time.
 * The rounding matters: right-clicking a single item should pick it up, not
 * pick up nothing.
 */
export function rightClickSlot(
  inv: Inventory,
  index: number,
  held: ItemStack | null,
): ItemStack | null {
  const slot = inv.get(index);

  if (held === null) {
    if (slot === null) return null;
    const take = Math.ceil(slot.count / 2);
    inv.takeFromSlot(index, take);
    return { id: slot.id, count: take };
  }

  if (slot === null) {
    inv.set(index, { id: held.id, count: 1 });
    const left = held.count - 1;
    return left > 0 ? { id: held.id, count: left } : null;
  }

  if (slot.id === held.id) {
    if (slot.count >= itemMaxStack(slot.id)) return held; // no room
    slot.count += 1;
    const left = held.count - 1;
    return left > 0 ? { id: held.id, count: left } : null;
  }

  // Different items: swap, same as left-click.
  inv.set(index, held);
  return slot;
}
